import React, { useState, useRef, useEffect } from 'react'
import { SECTIONS, FOOTER_FIELDS } from '../email/sections'
import { tonesFor, tone as toneOf } from '../email/theme'
import { sizedSrc } from '../email/html'
import EmailCodeField from '../../../../discounts/pickers/EmailCodeField'

// Editor for the email-native sections. Each section describes its own fields
// (email/sections.js), so a new section needs no editor code: text, textarea,
// url, image, segmented, toggle, tone, range and list fields render here.

const ICON = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true }
const IcCopy = () => (<svg {...ICON}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>)
const IcTrash = () => (<svg {...ICON}><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>)
const IcUp = () => (<svg {...ICON}><path d="M18 15l-6-6-6 6" /></svg>)
const IcDown = () => (<svg {...ICON}><path d="M6 9l6 6 6-6" /></svg>)
const IcX = () => (<svg {...ICON}><path d="M18 6L6 18M6 6l12 12" /></svg>)
const IcImage = () => (<svg {...ICON} width={15} height={15}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>)
const IcPlus = () => (<svg {...ICON}><path d="M12 5v14M5 12h14" /></svg>)

// Multi-line fields grow with their text (a fixed box clips the copy, and
// phones have no resize handle).
const GrowTextarea = React.forwardRef(({ value, onChange, rows = 3, ...rest }, fwd) => {
  const ref = useRef(null)
  const setRef = (el) => {
    ref.current = el
    if (typeof fwd === 'function') fwd(el)
    else if (fwd) fwd.current = el
  }
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + 2}px`
  }, [value])
  return <textarea ref={setRef} rows={rows} value={value} onChange={onChange} className="se-input se-textarea" {...rest} />
})

// Personalisation tags, inserted at the cursor
const TAGS = [
  { tag: '{{FirstName}}', label: 'First name' },
  { tag: '{{SalonName}}', label: 'Salon name' },
]
const TextareaField = ({ field, value, onChange }) => {
  const ref = useRef(null)
  const [focused, setFocused] = useState(false)
  const insert = (tag) => {
    const el = ref.current
    const v = value || ''
    const start = el ? el.selectionStart : v.length
    const end = el ? el.selectionEnd : v.length
    const next = v.slice(0, start) + tag + v.slice(end)
    onChange(next)
    setTimeout(() => {
      if (!el) return
      el.focus()
      const pos = start + tag.length
      el.setSelectionRange(pos, pos)
    }, 0)
  }
  return (
    <>
      <GrowTextarea
        ref={ref}
        rows={field.rows || 3}
        value={value || ''}
        placeholder={field.placeholder || ''}
        onChange={(e) => { const v = e.target.value; onChange(v) }}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
      />
      {focused && (
        <div className="se-tags">
          <span>Personalise:</span>
          {TAGS.map(t => (
            <button key={t.tag} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insert(t.tag)}>+ {t.label}</button>
          ))}
        </div>
      )}
    </>
  )
}

const LinkField = ({ value, onChange, placeholder, links }) => {
  const quick = [
    { label: 'Booking page', url: '{{BookingLink}}', note: 'Goes to your booking page, personalised for each client.' },
    links && links.GiftCardLink && { label: 'Gift vouchers', url: links.GiftCardLink, note: 'Goes to your gift voucher shop.' },
    links && links.ShopLink && { label: 'Shop', url: links.ShopLink, note: 'Goes to your online shop.' },
  ].filter(Boolean)
  const current = quick.find(q => q.url === value)
  return (
    <>
      <input
        type="text"
        className="se-input"
        value={value || ''}
        placeholder={placeholder || 'https://'}
        onChange={(e) => { const v = e.target.value; onChange(v) }}
        spellCheck={false}
      />
      <div className="se-chips">
        {quick.map(q => (
          <button key={q.label} type="button" className={`se-chip ${current === q ? 'is-on' : ''}`} onClick={() => onChange(q.url)}>
            {q.label}
          </button>
        ))}
      </div>
      {current && <p className="se-hint se-hint--teal">{current.note}</p>}
      {!current && value && !/^(https?:|mailto:|tel:|www\.|#|\{\{)/i.test(String(value).trim()) && (
        <p className="se-hint se-hint--warn">Links need to start with https://</p>
      )}
    </>
  )
}

const ImageField = ({ value, onPick, onClear, label }) => (
  value ? (
    <div className="se-img">
      <img src={sizedSrc(value, 320, 2.2)} alt="" />
      <div className="se-img-actions">
        <button type="button" onClick={onPick}><IcImage /> Change</button>
        <button type="button" onClick={onClear} aria-label={`Remove ${label ? label.toLowerCase() : 'image'}`}><IcX /></button>
      </div>
    </div>
  ) : (
    <button type="button" className="se-img-add" onClick={onPick}><IcImage /> Add {label ? label.toLowerCase() : 'image'}</button>
  )
)

const Segmented = ({ options, value, onChange }) => (
  <div className={`se-seg ${options.length >= 4 && options.some(o => String(o.label).length > 7) ? 'se-seg--grid' : ''}`} role="radiogroup">
    {options.map(o => (
      <button
        key={String(o.value)}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        className={value === o.value ? 'is-on' : ''}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
)

const Toggle = ({ checked, onChange, label }) => (
  <button type="button" role="switch" aria-checked={!!checked} className={`se-toggle ${checked ? 'is-on' : ''}`} onClick={() => onChange(!checked)}>
    <span className="se-toggle-label">{label}</span>
    <span className="se-switch" aria-hidden><span /></span>
  </button>
)

// Background swatches drawn from the email's own theme, so the choice is
// "the brand colour" rather than a hex code that fights the look.
const ToneField = ({ value, onChange, theme }) => (
  <div className="se-tones">
    {tonesFor(theme).concat(value === 'pop' && !theme.palette.pop ? [{ id: 'pop', label: 'Pop' }] : []).map(t => {
      const tn = toneOf(theme, t.id)
      return (
        <button key={t.id} type="button" className={`se-tone ${value === t.id ? 'is-on' : ''}`} onClick={() => onChange(t.id)} aria-pressed={value === t.id} title={t.label}>
          <span className="se-tone-swatch" style={{ background: tn.bg, color: tn.text }}>Aa</span>
          <span className="se-tone-label">{t.label}</span>
        </button>
      )
    })}
  </div>
)

const RangeField = ({ field, value, onChange }) => (
  <div className="se-range">
    <input type="range" min={field.min} max={field.max} step={field.step || 1} value={Number(value) || field.min} onChange={(e) => { const v = Number(e.target.value); onChange(v) }} />
    <span>{Number(value) || field.min}px</span>
  </div>
)

const itemTitle = (item, fallback) => {
  const t = item && (item.title || item.name || item.label || item.value)
  return t ? String(t).replace(/\{\{\w+\}\}/g, '').replace(/[*]/g, '').trim() || fallback : fallback
}

const ListField = ({ field, value, onChange, renderField }) => {
  const items = Array.isArray(value) ? value : []
  const [open, setOpen] = useState(0)
  const min = field.min || 0
  const max = field.max || 12
  const update = (i, key, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)))
  const move = (i, d) => {
    const j = i + d
    if (j < 0 || j >= items.length) return
    const next = items.slice()
    const [m] = next.splice(i, 1)
    next.splice(j, 0, m)
    onChange(next)
    setOpen(j)
  }
  const remove = (i) => {
    onChange(items.filter((_, idx) => idx !== i))
    setOpen(o => (o === i ? -1 : o > i ? o - 1 : o))
  }
  const add = () => {
    onChange([...items, JSON.parse(JSON.stringify(field.newItem || {}))])
    setOpen(items.length)
  }
  return (
    <div className="se-list">
      {items.map((it, i) => {
        const isOpen = open === i
        return (
          <div key={i} className={`se-item ${isOpen ? 'is-open' : ''}`}>
            <div className="se-item-head">
              <button type="button" className="se-item-toggle" onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen}>
                <span className="se-item-num">{i + 1}</span>
                <span className="se-item-title">{itemTitle(it, `${field.itemLabel || 'Item'} ${i + 1}`)}</span>
              </button>
              <div className="se-item-actions">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"><IcUp /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down"><IcDown /></button>
                <button type="button" onClick={() => remove(i)} disabled={items.length <= min} aria-label={`Remove ${(field.itemLabel || 'item').toLowerCase()}`}><IcX /></button>
              </div>
            </div>
            {isOpen && (
              <div className="se-item-body">
                {field.fields.filter(f => !f.show || f.show(it)).map(f => renderField(f, it[f.key], (v) => update(i, f.key, v), `${field.key}.${i}.${f.key}`))}
              </div>
            )}
          </div>
        )
      })}
      {items.length < max && (
        <button type="button" className="se-add" onClick={add}><IcPlus /> Add {(field.itemLabel || 'item').toLowerCase()}</button>
      )}
    </div>
  )
}

export const SectionEditor = ({ block, theme, links, onUpdate, onDelete, onDuplicate, onOpenImagePicker, onOpenBrand }) => {
  const isFooter = block.type === 'footer'
  const def = isFooter
    ? { label: 'Footer', description: 'Your sign-off and the legally required unsubscribe links', fields: FOOTER_FIELDS }
    : SECTIONS[block.type]
  if (!def) return null
  const c = block.content || {}
  const set = (key, value) => onUpdate(block.id, { [key]: value })

  const renderField = (f, value, change, path) => {
    const id = `se-${block.id}-${path}`
    const labelled = (control, { inlineLabel = false } = {}) => (
      <div className="se-field" key={path}>
        {!inlineLabel && <label className="se-label" htmlFor={id}>{f.label}</label>}
        {control}
        {f.hint && <p className="se-hint">{f.hint}</p>}
      </div>
    )
    switch (f.type) {
      case 'text':
        return labelled(<input id={id} type="text" className="se-input" value={value || ''} placeholder={f.placeholder || ''} onChange={(e) => { const v = e.target.value; change(v) }} />)
      case 'textarea':
        return labelled(<TextareaField field={f} value={value} onChange={change} />)
      case 'url':
        return labelled(<LinkField value={value} onChange={change} placeholder={f.placeholder} links={links} />)
      case 'image':
        return labelled(<ImageField value={value} label={f.label} onPick={() => onOpenImagePicker(path)} onClear={() => change('')} />)
      case 'segmented':
        return labelled(<Segmented options={f.options} value={value != null ? value : (f.options[0] && f.options[0].value)} onChange={change} />)
      case 'toggle':
        return labelled(<Toggle checked={value == null ? !!f.default : !!value} onChange={change} label={f.label} />, { inlineLabel: true })
      case 'tone':
        return labelled(<ToneField value={value || f.default || 'paper'} onChange={change} theme={theme} />)
      case 'range':
        return labelled(<RangeField field={f} value={value} onChange={change} />)
      case 'list':
        return labelled(<ListField field={f} value={value} onChange={change} renderField={renderField} />)
      case 'discountCode':
        // Several fields at once: the code fills in the offer around it
        return labelled(<EmailCodeField id={id} value={value} codeId={c.codeId} onPick={(patch) => onUpdate(block.id, patch)} />)
      default:
        return null
    }
  }

  const visible = def.fields.filter(f => !f.show || f.show(c))
  const contentFields = visible.filter(f => f.group !== 'style')
  const styleFields = visible.filter(f => f.group === 'style')

  return (
    <div className="se">
      <div className="se-top">
        <p className="se-desc">{def.description}</p>
        <div className="se-top-actions">
          {!isFooter && onDuplicate && <button type="button" onClick={() => onDuplicate(block.id)} aria-label="Duplicate section" title="Duplicate"><IcCopy /></button>}
          {!isFooter && onDelete && <button type="button" className="is-danger" onClick={() => onDelete(block.id)} aria-label="Delete section" title="Delete"><IcTrash /></button>}
        </div>
      </div>

      {block.type === 's-header' && (
        <div className="se-brandrow">
          {theme.logo
            ? <span className="se-brandrow-logo"><img src={theme.logo} alt="" /></span>
            : <span className="se-brandrow-empty">No logo yet</span>}
          <button type="button" onClick={onOpenBrand}>{theme.logo ? 'Change logo' : 'Add your logo'}</button>
        </div>
      )}

      {contentFields.map(f => renderField(f, c[f.key], (v) => set(f.key, v), f.key))}

      {styleFields.length > 0 && (
        <>
          <p className="se-group">Style</p>
          {styleFields.map(f => renderField(f, c[f.key], (v) => set(f.key, v), f.key))}
        </>
      )}

      {isFooter && (
        <div className="se-note">
          <strong>Added automatically</strong>
          Your salon name and address, your business details and the unsubscribe and preferences links. UK law requires them on every marketing email.
        </div>
      )}

      <button type="button" className="se-brandlink" onClick={onOpenBrand}>
        Colours, fonts and buttons are set once for the whole email in <span>Brand</span>
      </button>
    </div>
  )
}

export default SectionEditor
